// src/components/NavBar.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./NavBar.css";
import { ref, onValue } from "firebase/database";
import { db } from "../lib/firebase";
import { CONFIG } from "../config";

/** ---- Config ---- */
const IMAGES_BASE = CONFIG.IMAGES_BASE;

/* ---------- utils ---------- */
function isNonEmptyString(v) {
  return typeof v === "string" && v.trim() !== "";
}
function toImageSrc(val) {
  if (!isNonEmptyString(val)) return "";
  if (/^https?:\/\//i.test(val) || /^\/\//.test(val)) return val;
  if (val.startsWith("/")) return val;
  return `${IMAGES_BASE}/${encodeURIComponent(val)}`;
}
function slug(s) {
  return String(s || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}
function hasProtocol(href) {
  return /^[a-z][a-z0-9+.-]*:/i.test(String(href || ""));
}
function isExternal(href) {
  return (
    /^https?:\/\//i.test(href) ||
    href?.startsWith("//") ||
    hasProtocol(href)
  );
}
function normalizeNavHref(href) {
  if (!isNonEmptyString(href)) return "";
  const trimmed = href.trim();
  if (trimmed.startsWith("#")) return trimmed;
  if (isExternal(trimmed)) return trimmed;
  if (trimmed.startsWith("/")) return trimmed;
  return `/${trimmed.replace(/^\/+/, "")}`;
}

function shouldForceTopOnNav(to) {
  if (!isNonEmptyString(to)) return false;
  if (isExternal(to)) return false;
  return !to.includes("#");
}

function isTransparentColor(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return true;
  if (raw === "transparent") return true;
  if (raw === "rgba(0,0,0,0)" || raw === "rgba(0, 0, 0, 0)") return true;
  return false;
}

/* ---------- smart Link wrapper ---------- */
function DynamicLink({ to, className, style, onClick, children, ariaLabel }) {
  if (!to || to === "#" || isExternal(to)) {
    return (
      <a
        href={to || "#"}
        className={className}
        style={style}
        onClick={onClick}
        aria-label={ariaLabel}
      >
        {children}
      </a>
    );
  }
  return (
    <Link
      to={to}
      className={className}
      style={style}
      onClick={onClick}
      aria-label={ariaLabel}
    >
      {children}
    </Link>
  );
}

/* ---------- Homepage Section Link Component ---------- */
function HomepageSectionLink({ 
  section, 
  style, 
  onClick, 
  isMobile = false,
  handleNav 
}) {
  const location = useLocation();
  const navigate = useNavigate();

  const handleClick = (e) => {
    e.preventDefault();
    
    // Close mobile menu if applicable
    if (handleNav) handleNav();
    
    // If we're already on the homepage, scroll to the section
    if (location.pathname === "/") {
      const element = document.getElementById(section.sectionId);
      if (element) {
        // Smooth scroll to the section
        element.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
        
        // Update URL hash without page reload
        window.history.pushState(null, null, `#${section.sectionId}`);
      }
    } else {
      // Navigate to homepage with hash
      navigate(`/#${section.sectionId}`);
    }
    
    if (onClick) onClick(e);
  };

  return (
    <a
      href={`#${section.sectionId}`}
      className={`${isMobile ? 'mobile-link' : 'menu-link'} homepage-section-link`}
      style={style}
      onClick={handleClick}
      aria-label={`Scroll to ${section.label}`}
    >
      {section.label}
      <span className="section-link-icon" aria-hidden="true">↓</span>
    </a>
  );
}

export default function NavBar() {
  const location = useLocation();
  const headerRef = useRef(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== "undefined" ? window.innerWidth >= 992 : true
  );
  const [navHeight, setNavHeight] = useState(64);
  const [loading, setLoading] = useState(true);

  const [navCfg, setNavCfg] = useState(null);

  // Live subscribe to /navconfig
  useEffect(() => {
    const off = onValue(
      ref(db, "navconfig"),
      (snap) => {
        const v = snap.val();

        if (!v || typeof v !== "object") {
          setNavCfg(null);
          setLoading(false);
          return;
        }

        setNavCfg({
          brand: v.brand || v.brandname || "",
          brandColor: isNonEmptyString(v.brandColor) ? v.brandColor : undefined,
          brandFontSize:
            typeof v.brandFontSize === "number" && !Number.isNaN(v.brandFontSize)
              ? v.brandFontSize
              : undefined,
          linkColor: isNonEmptyString(v.linkColor) ? v.linkColor : undefined,
          linkFontSize:
            typeof v.linkFontSize === "number" && !Number.isNaN(v.linkFontSize)
              ? v.linkFontSize
              : undefined,
          logo: isNonEmptyString(v.logo) ? v.logo : "",
          logoHeight:
            typeof v.logoHeight === "number" && !Number.isNaN(v.logoHeight)
              ? v.logoHeight
              : undefined,
          dropdowns: Array.isArray(v.dropdowns) ? v.dropdowns : [],
          buttons: Array.isArray(v.buttons) ? v.buttons : [],
          pages: Array.isArray(v.pages) ? v.pages : [],
          backgroundcolor: isNonEmptyString(v.backgroundcolor)
            ? v.backgroundcolor
            : undefined,
          menuAlign: ["left", "center", "right"].includes(
            (v.menuAlign || "").toLowerCase()
          )
            ? v.menuAlign.toLowerCase()
            : "left",
        });
        setLoading(false);
      },
      (err) => {
        console.error("navconfig load error:", err);
        setNavCfg(null);
        setLoading(false);
      }
    );
    return () => off();
  }, []);

  // Responsive handling
  useEffect(() => {
    const onResize = () => {
      const wide = window.innerWidth >= 992;
      setIsDesktop(wide);
      if (wide) setMobileOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Handle scrolling to section on page load if there's a hash
  useEffect(() => {
    if (location.hash && location.pathname === "/") {
      const hash = location.hash.substring(1); // Remove the #
      const element = document.getElementById(hash);
      if (element) {
        // Small delay to ensure page is fully loaded
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname, location.search, location.hash]);

  const handleNav = () => {
    if (!isDesktop) setMobileOpen(false);
  };

  useEffect(() => {
    if (!isDesktop || !mobileOpen) return undefined;
    setMobileOpen(false);
    return undefined;
  }, [isDesktop, mobileOpen]);

  useEffect(() => {
    if (!mobileOpen || isDesktop) return undefined;
    const html = document.documentElement;
    const prevHtmlOverflow = html.style.overflow;
    const prevOverflow = document.body.style.overflow;
    const prevTouchAction = document.body.style.touchAction;
    html.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    document.body.style.touchAction = "none";
    return () => {
      html.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevOverflow;
      document.body.style.touchAction = prevTouchAction;
    };
  }, [mobileOpen, isDesktop]);

  useEffect(() => {
    if (!mobileOpen || isDesktop) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setMobileOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [mobileOpen, isDesktop]);

  useEffect(() => {
    const measure = () => {
      const next = Math.ceil(headerRef.current?.getBoundingClientRect().height || 64);
      setNavHeight(next > 0 ? next : 64);
    };

    measure();
    window.addEventListener("resize", measure);

    let observer = null;
    if (typeof ResizeObserver !== "undefined" && headerRef.current) {
      observer = new ResizeObserver(measure);
      observer.observe(headerRef.current);
    }

    return () => {
      window.removeEventListener("resize", measure);
      if (observer) observer.disconnect();
    };
  }, [navCfg, isDesktop]);

  const handleRegularNavClick = (to) => () => {
    handleNav();
    if (!shouldForceTopOnNav(to)) return;

    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    const targets = [
      document.scrollingElement,
      document.documentElement,
      document.body,
      document.querySelector(".app-root"),
      document.querySelector(".app-main"),
    ].filter(Boolean);

    targets.forEach((el) => {
      el.scrollTop = 0;
      el.scrollLeft = 0;
    });
  };

  // Groups (previous dropdowns) → now direct links to /gallery/:groupSlug
  const GROUPS = useMemo(() => {
    const groups = Array.isArray(navCfg?.dropdowns) ? navCfg.dropdowns : [];
    return groups
      .filter((g) => isNonEmptyString(g?.name))
      .map((g) => ({
        label: g.name.trim(),
        href: `/gallery/${slug(g.name)}`,
      }));
  }, [navCfg?.dropdowns]);

  // Separate regular pages from homepage sections
  const { REGULAR_PAGES, HOMEPAGE_SECTIONS } = useMemo(() => {
    const arr = Array.isArray(navCfg?.pages) ? navCfg.pages : [];
    
    const regularPages = arr.filter(
      (p) => p?.show && 
             (!p.type || p.type === "page") && 
             isNonEmptyString(p?.label) && 
             isNonEmptyString(p?.href)
    ).map((p) => ({
      ...p,
      href: normalizeNavHref(p.href),
    }));
    
    const homepageSections = arr.filter(
      (p) => p?.show && 
             p.type === "homepage" && 
             isNonEmptyString(p?.label) && 
             isNonEmptyString(p?.sectionId)
    );
    
    return { REGULAR_PAGES: regularPages, HOMEPAGE_SECTIONS: homepageSections };
  }, [navCfg?.pages]);

  // Right-side CTAs
  const rightButtons = useMemo(() => {
    const btns = Array.isArray(navCfg?.buttons) ? navCfg.buttons : [];
    return btns.filter(
      (b) => isNonEmptyString(b?.name) && isNonEmptyString(b?.link)
    );
  }, [navCfg?.buttons]);

  const brandLogoSrc = useMemo(() => toImageSrc(navCfg?.logo), [navCfg?.logo]);

  const MENU_ALIGN_CLASS =
    navCfg?.menuAlign === "center"
      ? "menu-align-center"
      : navCfg?.menuAlign === "right"
      ? "menu-align-right"
      : "menu-align-left";

  const linkStyle = {
    color: navCfg?.linkColor,
    fontSize: navCfg?.linkFontSize ? `${navCfg.linkFontSize}px` : undefined,
  };
  const mobileLinkStyle = {
    ...linkStyle,
    borderColor: navCfg?.linkColor || "#d1d5db",
  };
  const drawerBackground = useMemo(() => {
    if (!isTransparentColor(navCfg?.backgroundcolor)) return navCfg.backgroundcolor;
    return "#1f2937";
  }, [navCfg?.backgroundcolor]);

  const mobileDrawerStyle = {
    "--mobile-drawer-bg": drawerBackground,
    "--mobile-link-color": navCfg?.linkColor || "#111827",
    "--mobile-link-border": navCfg?.linkColor || "#d1d5db",
    background: drawerBackground,
    color: navCfg?.linkColor || "#111827",
    borderRightColor: navCfg?.linkColor || "#d1d5db",
  };

  if (loading) {
    return (
      <header className="nav-wrapper nav-sticky">
        <div className="nav-container nav-loading-shell">
          <div className="nav-loading-state">Loading...</div>
        </div>
      </header>
    );
  }

  if (!navCfg) return null;

  return (
    <header
      ref={headerRef}
      className="nav-wrapper nav-sticky"
      style={{
        background: navCfg.backgroundcolor || "transparent",
        "--nav-height": `${navHeight}px`,
      }}
    >
      <div className="nav-container">
        <DynamicLink to="/" className="brand" onClick={handleNav} ariaLabel="Home">
          {isNonEmptyString(brandLogoSrc) && (
            <img
              src={brandLogoSrc}
              alt="Logo"
              className="brand-logo"
              style={{ height: `${navCfg.logoHeight || 40}px`, width: "auto" }}
            />
          )}
          {isNonEmptyString(navCfg.brand) && (
            <span
              className="brand-text"
              style={{
                color: navCfg.brandColor,
                fontSize: navCfg.brandFontSize
                  ? `${navCfg.brandFontSize}px`
                  : undefined,
              }}
            >
              {navCfg.brand}
            </span>
          )}
        </DynamicLink>

        {/* Desktop Navigation */}
        <nav className="nav-desktop">
          <ul className={`menu ${MENU_ALIGN_CLASS}`}>
            {/* Regular Pages first */}
            {REGULAR_PAGES.map((p) => (
              <li key={`page-${p.label}`} className="menu-item">
                <DynamicLink
                  to={p.href}
                  className="menu-link"
                  style={linkStyle}
                  onClick={handleRegularNavClick(p.href)}
                >
                  {p.label}
                </DynamicLink>
              </li>
            ))}

            {/* Homepage Sections */}
            {HOMEPAGE_SECTIONS.map((section) => (
              <li key={`homepage-${section.sectionId}`} className="menu-item">
                <HomepageSectionLink
                  section={section}
                  style={linkStyle}
                  handleNav={handleNav}
                />
              </li>
            ))}

            {/* Groups (no dropdown, direct link) */}
            {GROUPS.map((g) => (
              <li key={`group-${g.label}`} className="menu-item">
                <DynamicLink
                  to={g.href}
                  className="menu-link"
                  style={linkStyle}
                  onClick={handleRegularNavClick(g.href)}
                >
                  {g.label}
                </DynamicLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* Right-side Buttons + Mobile Burger */}
        <div className="right-actions">
          {rightButtons.map((b, i) => (
            <DynamicLink
              key={`${b.name}-${i}`}
              to={b.link}
              className="cta"
              onClick={handleRegularNavClick(b.link)}
            >
              {b.name}
            </DynamicLink>
          ))}

          <button
            type="button"
            className={`burger ${mobileOpen ? "is-open" : ""}`}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
            aria-expanded={mobileOpen}
            aria-controls="main-mobile-drawer"
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </div>

      {/* Mobile Drawer */}
      <button
        type="button"
        className={`mobile-backdrop ${mobileOpen ? "open" : ""}`}
        aria-label="Close mobile menu"
        onClick={() => setMobileOpen(false)}
      />
      <div
        id="main-mobile-drawer"
        className={`mobile-drawer ${mobileOpen ? "open" : ""}`}
        style={mobileDrawerStyle}
      >
        <ul className="mobile-list">
          {/* Regular Pages */}
          {REGULAR_PAGES.map((p) => (
            <li key={`mp-${p.label}`} className="mobile-item">
              <DynamicLink
                to={p.href}
                className="mobile-link"
                style={mobileLinkStyle}
                onClick={handleRegularNavClick(p.href)}
              >
                {p.label}
              </DynamicLink>
            </li>
          ))}

          {/* Homepage Sections */}
          {HOMEPAGE_SECTIONS.map((section) => (
            <li key={`mh-${section.sectionId}`} className="mobile-item">
              <HomepageSectionLink
                section={section}
                style={mobileLinkStyle}
                isMobile={true}
                handleNav={handleNav}
              />
            </li>
          ))}

          {/* Groups (no accordion, direct links) */}
          {GROUPS.map((g) => (
            <li key={`mg-${g.label}`} className="mobile-item">
              <DynamicLink
                to={g.href}
                className="mobile-link"
                style={mobileLinkStyle}
                onClick={handleRegularNavClick(g.href)}
              >
                {g.label}
              </DynamicLink>
            </li>
          ))}

          {/* Mobile CTAs */}
          {rightButtons.length > 0 && (
            <li className="mobile-cta-wrap">
              {rightButtons.map((b, i) => (
                <DynamicLink
                  key={`${b.name}-m-${i}`}
                  to={b.link}
                  className="cta block"
                  onClick={handleRegularNavClick(b.link)}
                >
                  {b.name}
                </DynamicLink>
              ))}
            </li>
          )}
        </ul>
      </div>
    </header>
  );
}
